import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Permissions } from '@/auth/permissions';
import { useHasPermission } from '@/auth/RequirePermission';
import { cn } from '@/lib/utils';

/**
 * The tabs across the top of the settings area (M2-20): the company's own
 * settings, and its users. The main navigation keeps one "Settings" entry; this
 * is how the two pages reach each other.
 *
 * Each tab is shown only to whoever its route admits: Company is
 * `company.manage` (the route `/settings` itself), Users is `users.manage`.
 * Owner and Admin hold both today, so both see both.
 */
export function SettingsNav() {
  const { t } = useTranslation();
  const hasPermission = useHasPermission();
  const tabs = [
    { to: '/settings', label: t('settings.nav.company'), permission: Permissions.manageCompany },
    { to: '/settings/users', label: t('settings.nav.users'), permission: Permissions.manageUsers },
  ].filter((tab) => hasPermission(tab.permission));

  return (
    <nav aria-label={t('settings.nav.label')} className="border-b border-[var(--color-border)]">
      <ul className="-mb-px flex gap-4">
        {tabs.map((tab) => (
          <li key={tab.to}>
            <NavLink
              to={tab.to}
              end
              className={({ isActive }) =>
                cn(
                  'inline-flex min-h-11 items-center border-b-2 px-1 text-sm font-medium focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none',
                  isActive
                    ? 'border-[var(--color-primary)] text-[var(--color-foreground)]'
                    : 'border-transparent text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]',
                )
              }
            >
              {tab.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
